import React, { useState } from 'react';
import { SourceSelector } from '../components/SourceSelector.tsx';
import { JobStatusCard } from '../components/JobStatusCard.tsx';
import { uploadZip, startTranscriptionJob } from '../../../request/transcription/transcription.api.ts';
import type { OutputFormat, SourceType, WhisperModel } from '../../../request/transcription/transcription.api.ts';
import toast from 'react-hot-toast';

type PageState = 'form' | 'running';

export default function TranscriptionPage() {
  const [pageState, setPageState] = useState<PageState>('form');
  const [jobId, setJobId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = async (params: {
    sourceType: SourceType;
    sourceConfig: any;
    model: WhisperModel;
    language: string;
    formats: OutputFormat[];
    zipFile?: File;
  }) => {
    setLoading(true);
    setUploadProgress(0);

    try {
      let sourceConfig = params.sourceConfig;

      // Upload ZIP first if source is zip
      if (params.sourceType === 'zip' && params.zipFile) {
        toast.loading('Uploading ZIP…', { id: 'upload' });
        const { gcs_file_ref } = await uploadZip(params.zipFile, setUploadProgress);
        toast.success('ZIP uploaded', { id: 'upload' });
        sourceConfig = { gcs_file_ref };
      }

      toast.loading('Starting transcription job…', { id: 'job' });
      const id = await startTranscriptionJob({
        sourceType: params.sourceType,
        sourceConfig,
        model: params.model,
        language: params.language || undefined,
        formats: params.formats,
      });

      toast.success('Job queued', { id: 'job' });
      setJobId(id);
      setPageState('running');
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? err?.message ?? 'Failed to start job', { id: 'job' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Video / Audio Transcription</h1>
        <p className="text-sm text-gray-500 mt-1">
          Convert videos or audio files to DOCX and PDF transcripts using Whisper.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        {pageState === 'form' ? (
          <>
            {loading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Uploading ZIP…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-indigo-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            <SourceSelector onSubmit={handleSubmit} loading={loading} />
          </>
        ) : (
          jobId && (
            <JobStatusCard
              jobId={jobId}
              onReset={() => { setPageState('form'); setJobId(null); }}
            />
          )
        )}
      </div>
    </div>
  );
}
